<template>
  <div class="profile-dropdown-wrapper">
    <VaDropdown v-model="isShown" :offset="[9, 0]" class="profile-dropdown" stick-to-edges>
      <template #anchor>
        <VaButton preset="secondary" color="textPrimary">
          <span class="profile-dropdown__anchor min-w-max">
            <slot />
            <VaAvatar :size="32" color="primary">
              <i class="fas fa-user-circle fa-lg text-white"></i>
            </VaAvatar>
          </span>
        </VaButton>
      </template>
      <VaDropdownContent
        class="profile-dropdown__content md:w-60 px-0 py-4 w-full"
        :style="{ '--hover-color': hoverColor }"
      >
        <VaList v-for="group in options" :key="group.name">
          <header v-if="group.name" class="uppercase text-[var(--va-secondary)] opacity-80 font-bold text-xs px-4">
            {{ t(`user.${group.name}`) }}
          </header>
          <VaListItem
            v-for="item in group.list"
            :key="item.name"
            class="menu-item px-4 text-base cursor-pointer h-8"
            v-bind="resolveLinkAttribute(item)"
            @click="handleItemClick(item)"
          >
            <VaIcon :name="item.icon" class="pr-1" color="secondary" />
            {{ t(`user.${item.name}`) }}
          </VaListItem>
          <VaListSeparator v-if="group.separator" class="mx-3 my-2" />
        </VaList>
      </VaDropdownContent>
    </VaDropdown>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { useColors } from 'vuestic-ui'
import { useHemocultureAuthStore } from '@/stores/hemoculture-auth'

const { colors, setHSLAColor } = useColors()
const hoverColor = computed(() => setHSLAColor(colors.focus, { a: 0.1 }))

const { t } = useI18n()
const router = useRouter()
const authStore = useHemocultureAuthStore()

type ProfileListItem = {
  name: string
  to?: string
  href?: string
  icon: string
}

type ProfileOptions = {
  name: string
  separator: boolean
  list: ProfileListItem[]
}

withDefaults(
  defineProps<{
    options?: ProfileOptions[]
  }>(),
  {
    options: () => [
      {
        name: 'account',
        separator: true,
        list: [
          {
            name: 'profile',
            to: 'preferences',
            icon: 'mso-account_circle',
          },
          // {
          //   name: 'settings',
          //   to: 'settings',
          //   icon: 'mso-settings',
          // },
        ],
      },
      {
        name: '',
        separator: false,
        list: [
          {
            name: 'logout',
            to: 'login',
            icon: 'mso-logout',
          },
        ],
      },
    ],
  },
)

const isShown = ref(false)

const handleItemClick = async (item: ProfileListItem) => {
  if (item.name === 'logout') {
    await authStore.logout()
    router.replace({ name: 'login' })
  }
}

const resolveLinkAttribute = (item: ProfileListItem) => {
  if (item.name === 'logout') {
    return {}
  }
  return item.to ? { to: { name: item.to } } : item.href ? { href: item.href, target: '_blank' } : {}
}
</script>

<style lang="scss" scoped>
.profile-dropdown-wrapper {
  :deep(.va-dropdown__anchor) {
    transition: all 0.3s ease;

    &:hover {
      transform: translateY(-2px);
    }
  }
}

.profile-dropdown {
  cursor: pointer;

  :deep(.va-button) {
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%);
    border: 1px solid rgba(255, 255, 255, 0.2);
    color: white;
    transition: all 0.3s ease;

    &:hover {
      background: linear-gradient(135deg, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0.1) 100%);
      border-color: rgba(255, 255, 255, 0.3);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    }

    &:active {
      transform: scale(0.98);
    }
  }

  &__content {
    background: var(--va-background-secondary);
    border-radius: 0.75rem;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
    border: 1px solid var(--va-background-primary);
    animation: dropdown-enter 0.2s ease;

    @keyframes dropdown-enter {
      from {
        opacity: 0;
        transform: translateY(-8px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    header {
      padding: 0.75rem 1rem;
      background: linear-gradient(135deg, rgba(15, 34, 204, 0.05) 0%, rgba(79, 70, 229, 0.03) 100%);
      border-bottom: 1px solid rgba(15, 34, 204, 0.1);
      margin-bottom: 0.5rem;
      font-size: 0.7rem;
      letter-spacing: 0.05em;
    }

    .menu-item {
      transition: all 0.2s ease;
      border-radius: 0.375rem;
      margin: 0.25rem 0.5rem;

      &:hover {
        background: linear-gradient(135deg, rgba(15, 34, 204, 0.1) 0%, rgba(79, 70, 229, 0.05) 100%);
        transform: translateX(4px);
        color: var(--va-primary);

        :deep(.va-icon) {
          color: var(--va-primary) !important;
        }
      }

      :deep(.va-icon) {
        transition: all 0.2s ease;
      }
    }
  }

  &__anchor {
    display: inline-block;
    transition: all 0.3s ease;

    &:hover {
      transform: translateY(-2px);
    }
  }
}
</style>
